// SYSTEMS PER AREA on the Levels, Areas & Systems page (owner, 2026-10-13:
// "add systems per area ... also suggest systems ... from mapped
// construction tracking to areas using the sheet overlay and elements
// tagging per area").
//
// One area's systems (tags, removable by whoever may edit areas), a picker
// over the project's one system vocabulary that also takes a new name, and
// the systems the area's TAGGED MODEL ELEMENTS suggest - each with its
// element count, Accept / Dismiss, and Accept all. The counts and the
// categories that map to no system are printed as the server sends them:
// the suggestion is read from every tagged element, and the panel says how
// many that was, so nothing is silently left out.
//
// `row` is one entry of GET /areas/systems; `onChanged(reply)` receives the
// route's answer for this area ({ systems, suggestions, unmapped, ... }).
import React, { useState } from "react";
import { Button, Select, Tag, Tooltip, message } from "antd";
import { CheckOutlined, CloseOutlined, PlusOutlined, UndoOutlined } from "@ant-design/icons";
import { setAreaSystems, acceptAreaSystems, dismissAreaSystem } from "api/areas/areasApi";

const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const S = { muted: "#64748b", faint: "#94a3b8", line: "#e5e7eb" };

// What the panel shows, from one row - split out so it can be tested
// without rendering antd.
export function systemsView(row) {
  const r = row || {};
  const suggestions = Array.isArray(r.suggestions) ? r.suggestions : [];
  return {
    systems: Array.isArray(r.systems) ? r.systems : [],
    open: suggestions.filter((s) => s.state === "new"),
    dismissed: suggestions.filter((s) => s.state === "dismissed"),
    unmapped: Array.isArray(r.unmapped) ? r.unmapped : [],
    elements: Number(r.elements) || 0,
    mapped: Number(r.mapped) || 0,
  };
}

// THE AUTOMATIC TAGGING, in one line above the areas (GET /areas/systems
// `placement`, written by the tagging run): how many elements it tagged, or
// which drawing still has to be placed on the model before it can. The
// server writes the sentence; this only decides whether there is one.
export function placementLine(placement) {
  const p = placement || null;
  if (!p || !p.why) return "";
  return String(p.why);
}
export function placementNeedsAction(placement) {
  const p = placement || {};
  return (p.sheets || []).some((s) => !s.placedOnModel) || (p.status && p.status !== "done");
}

export const unmappedLine = (unmapped) => (unmapped || [])
  .map((u) => `${u.category} (${fmt(u.elements)})`).join(", ");

const sourceTip = (s) => {
  if (s.source === "suggested") return `Accepted from the tagged model elements${s.elements != null ? ` (${fmt(s.elements)} then)` : ""}${s.addedByName ? ` by ${s.addedByName}` : ""}`;
  if (s.source === "carried") return "Carried over from the earlier drawing of this area";
  return s.addedByName ? `Added by ${s.addedByName}` : "Added by hand";
};

export default function AreaSystemsPanel({
  projectId, areaId, row, vocabulary = [], canEdit = false, compact = false, trackingHref = "", onChanged,
}) {
  const [busy, setBusy] = useState(false);
  const v = systemsView(row);
  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      if (onChanged) onChanged(r);
      if (ok) message.success(ok(r));
    } catch (e) {
      message.error(e.message || "Could not change the systems");
    } finally {
      setBusy(false);
    }
  };
  const add = (name) => {
    const n = String(name || "").trim();
    if (!n) return;
    run(() => setAreaSystems(projectId, areaId, { add: [n] }));
  };
  const remove = (name) => run(() => setAreaSystems(projectId, areaId, { remove: [name] }));
  const accept = (names) => run(
    () => acceptAreaSystems(projectId, areaId, names === "all" ? { all: true } : { names }),
    (r) => `${((r && r.accepted) || []).map((x) => x.name).join(", ") || "Nothing"} added to this area's systems`,
  );
  const dismiss = (name, undo = false) => run(() => dismissAreaSystem(projectId, areaId, { name, undo }));

  const held = new Set(v.systems.map((s) => String(s.name).toLowerCase()));

  if (compact) {
    if (!v.systems.length && !v.open.length) return null;
    return (
      <div style={{ fontSize: 12, marginTop: 3, color: "#334155" }} data-testid="area-systems-compact">
        {v.systems.map((s) => <Tag key={s.name} color="blue" style={{ marginRight: 4 }}>{s.name}</Tag>)}
        {v.open.length > 0 && (
          <span style={{ color: "#b45309" }}>{v.open.length} suggested from {fmt(v.elements)} tagged elements</span>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8, borderTop: `1px dashed ${S.line}`, paddingTop: 6 }} data-testid="area-systems">
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Systems</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
        {v.systems.length === 0 && <span style={{ fontSize: 12, color: S.faint }}>None yet</span>}
        {v.systems.map((s) => (
          <Tooltip key={s.name} title={sourceTip(s)}>
            <Tag
              color="blue"
              closable={canEdit && !busy}
              onClose={(e) => { e.preventDefault(); remove(s.name); }}
              style={{ marginRight: 0 }}
            >
              {s.name}
            </Tag>
          </Tooltip>
        ))}
      </div>
      {canEdit && (
        <Select
          mode="tags"
          size="small"
          style={{ width: "100%", marginBottom: 6 }}
          placeholder={<span><PlusOutlined /> Add a system (pick or type a new one)</span>}
          value={[]}
          disabled={busy}
          onSelect={(val) => add(val)}
          options={(vocabulary || []).filter((o) => !held.has(String(o.value).toLowerCase())).map((o) => ({ value: o.value, label: o.label }))}
        />
      )}

      <div style={{ fontSize: 12, color: S.muted, marginBottom: 4 }}>
        {v.elements > 0 ? (
          <>
            Suggested from <b>{fmt(v.elements)}</b> model element{v.elements === 1 ? "" : "s"} tagged to this area
            {" "}({fmt(v.mapped)} in a system)
          </>
        ) : (
          <>
            No model elements are tagged to this area yet.
            {trackingHref && (
              <> Tag them over the sheet in <a href={trackingHref}>Construction tracking</a>.</>
            )}
          </>
        )}
      </div>
      {v.open.map((s) => (
        <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginBottom: 3 }}>
          <span style={{ flex: 1 }}>
            <b>{s.name}</b> - {fmt(s.elements)} element{s.elements === 1 ? "" : "s"}
            {s.changed && (
              <Tooltip title="You dismissed this before; the elements tagged to this area have changed since">
                <span style={{ color: S.faint }}> (back: elements changed)</span>
              </Tooltip>
            )}
          </span>
          {canEdit && (
            <>
              <Button size="small" type="primary" ghost icon={<CheckOutlined />} disabled={busy} onClick={() => accept([s.name])}>Accept</Button>
              <Button size="small" icon={<CloseOutlined />} disabled={busy} onClick={() => dismiss(s.name)}>Dismiss</Button>
            </>
          )}
        </div>
      ))}
      {canEdit && v.open.length > 1 && (
        <Button size="small" type="primary" disabled={busy} onClick={() => accept("all")} style={{ marginTop: 2 }}>
          Accept all {v.open.length}
        </Button>
      )}
      {v.dismissed.length > 0 && (
        <div style={{ fontSize: 11.5, color: S.faint, marginTop: 4 }}>
          Dismissed:{" "}
          {v.dismissed.map((s, i) => (
            <span key={s.name}>
              {i > 0 && ", "}
              {s.name} ({fmt(s.elements)})
              {canEdit && (
                <Tooltip title="Show this suggestion again">
                  <UndoOutlined style={{ marginLeft: 3, cursor: "pointer" }} onClick={() => dismiss(s.name, true)} />
                </Tooltip>
              )}
            </span>
          ))}
        </div>
      )}
      {v.unmapped.length > 0 && (
        <div style={{ fontSize: 11.5, color: S.muted, marginTop: 4 }} data-testid="area-systems-unmapped">
          Unmapped categories: {unmappedLine(v.unmapped)}
        </div>
      )}
    </div>
  );
}
